import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { participantPages } from '@app/pages/participant/participants.routes';
import { CompactViewComponent } from '@app/workflow/components/navigator/compact-view/compact-view.component';
import { INavTreeNode } from '@app/workflow/components/navigator/nav-tree/interfaces';
import { NavTreeBuilder } from '@app/workflow/components/navigator/nav-tree/nav-tree-builder';
import { NavTreeNavigator } from '@app/workflow/components/navigator/nav-tree/nav-tree-navigator';
import { WizardPageTreeNode } from '@app/workflow/components/navigator/nav-tree/nav-tree-nodes';
import { IQuestionnaireWorkflowItem } from '@interfaces/interfaces';
import { TWorkflowViewMode } from '@workflow/components/navigator/interfaces';
import { SpecialQuestionService } from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { WorkflowItemDirective } from '@workflow/directives/workflow-item/workflow-item.directive';
import { WorkflowService } from '@workflow/services/workflow.service';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, distinctUntilKeyChanged, filter, first, map, shareReplay, startWith, switchMap, switchMapTo, tap } from 'rxjs/operators';
import { WizardPage } from './wizard-view/interfaces';
import { QuestionStatusService } from '@app/pages/questions/question-status.service';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import { EWorkflowPathKey } from '@app/pages/participant/workflow/workflow-path-key.enum';

@Component({
	selector: 'workflow-url-navigator',
	templateUrl: './workflow-url-navigator.component.html',
	styleUrls: ['./workflow-url-navigator.component.scss']
})
export class WorkflowUrlNavigatorComponent implements OnDestroy, OnInit {

	viewMode: TWorkflowViewMode = null;
	workflowItem: IQuestionnaireWorkflowItem = null;

	@ViewChild(WorkflowItemDirective)
	set workflowItemDirective(workflowItemDirective: WorkflowItemDirective) {
		this.workflowItemDirective$.next(workflowItemDirective);
	}
	private workflowItemDirective$ = new ReplaySubject<WorkflowItemDirective>(1);

	@ViewChild(CompactViewComponent)
	private compactView: CompactViewComponent;

	private subscription = new Subscription();

	private navTree: INavTreeNode;

	currentNode: INavTreeNode;
	currentPage: WizardPage;

	constructor(
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private questionStatusService: QuestionStatusService,
		private specialQuestionService: SpecialQuestionService,
		private workflowService: WorkflowService,
		private workflowItemService: WorkflowItemNavigationService,
		private navTreeBuilder: NavTreeBuilder,
		private navigator: NavTreeNavigator,
	) {	}

	get workflowItemId$(): Observable<string> {
		return this.activatedRoute.paramMap.pipe(
			map(params => params.get('itemId')),
			filter(param => !!param),
			distinctUntilChanged(),
		);
	}

	get workflowItem$(): Observable<IQuestionnaireWorkflowItem> {
		return this.workflowItemId$.pipe(
			switchMap(workflowItemId => {
				return this.workflowService.workflow$.pipe(
					filter(wf => !!wf),
					map(workflow => {
						const workflowItem = workflow.items.find(item => item.id === workflowItemId);
						if (!workflowItem) {
							this.navigateToParticipantsDashboard();
							return null;
						}
						if (workflowItem.type !== 'Questionnaire') {
							console.error('Supports only items typed "Questionnaire"');
							this.navigateToParticipantsDashboard();
							return null;
						}
						return workflowItem as IQuestionnaireWorkflowItem;
					}),
					filter(wfItem => !!wfItem),
					tap(wfItem => this.workflowItem = wfItem),
				);
			}),
			shareReplay({refCount: true, bufferSize: 1}),
		);
	}

	private get urlPrefix$() {
		return this.activatedRoute.firstChild.url.pipe(
			map((segments: UrlSegment[]) => segments.map(segment => segment.path)),
			map(segments => '/' + segments.join('/')),
		);
	}

	ngOnInit(): void {
		const navTreePipe = this.workflowItem$.pipe(
			tap((workflowItem) => this.buildNavTree(workflowItem)),
		);

		const urlPrefixWithParams$ = this.router.events.pipe(
			filter(e => e instanceof NavigationEnd),
			startWith(() => null),
			filter(() => !!this.activatedRoute.firstChild),
			switchMap(() => {
				return combineLatest([
					this.urlPrefix$,
					this.activatedRoute.queryParams,
				]).pipe(
					first(),
				);
			}),
		);

		const currentNode$ = navTreePipe.pipe(
			switchMapTo(
				combineLatest([
					urlPrefixWithParams$,
					this.workflowItemDirective$.pipe(switchMap(workflowItemDirective => workflowItemDirective.current$)),
					this.workflowItemDirective$.pipe(switchMap(workflowItemDirective => workflowItemDirective.workflowStatus$)),
				])
			),
			filter(([[prefix, ], data, status]) => !!prefix && !!data && !!status),
			map(([[prefix, params], data, status]) => {
				let current = this.navTree;
				if (prefix !== '' && prefix !== '/') {
					current = this.navigator.findNode(this.navTree, prefix, { allowBranch: true });
				}

				const forcedViewMode = this.parseViewMode(params['view']);
				const viewMode = this.viewMode = this.selectViewMode(forcedViewMode);

				if (current) {

					const finished = status.completed;
					const completed = current.isCompleted(data);

					const continueWorkflow = params['continue'] === 'true';
					let currentValid = this.navigator.findValidNode(current, data, { firstIncomplete: continueWorkflow && !completed && !finished, finished: finished || completed });
					if (currentValid) {

						// Bei Fortsetzung prüfen, ob Fragegruppe 'unstarted' ist.
						// Wenn ja, springe an Anfang von Prolog
						if (continueWorkflow) {
							const questionGroupStatus = this.questionStatusService.checkQuestionGroupStatus((currentValid as WizardPageTreeNode).page, data);

							if (questionGroupStatus.status === 'unstarted') {
								const prolog = this.navigator.findPrevProlog(currentValid.parent, data, { getFirstPage: true });
								currentValid = prolog || currentValid;
							}
						}

						// Weiterleitung zur korrekten Seite
						if (currentValid !== current) {
							this.router
								.navigate([participantPages.workflowProcess.base({wfItemId: this.workflowItem.id, pageUrl: currentValid.urlPrefixes[0]})], {
									queryParams: { view: viewMode },
									queryParamsHandling: 'merge',
									replaceUrl: true,
								})
								.catch(console.error);
						}

						return currentValid;
					}
				}
				
				this.router.navigate([this.workflowItemService.getFirstAvailableExitURL(this.workflowItem, EWorkflowPathKey.OVERVIEW, viewMode)], { 
					replaceUrl: true 
				}).catch(console.error);
				return null;
			}),
			filter(current => !!current),
			distinctUntilChanged(),
			shareReplay({refCount: true, bufferSize: 1}),
		);

		this.subscription.add(currentNode$.subscribe(current => {
			this.compactView?.scrollToNode(current);
			this.currentNode = current;
			this.currentPage = (<WizardPageTreeNode> current).page;
		}));

		const viewParameterPipe = this.activatedRoute.queryParams.pipe(
			distinctUntilKeyChanged('view'),
			map(params => this.parseViewMode(params['view'])),
			tap(view => this.viewMode = this.selectViewMode(view)),
		);

		this.subscription.add(viewParameterPipe.subscribe());
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private parseViewMode(viewModeParam: string): TWorkflowViewMode {
		const viewParameter = viewModeParam?.toLowerCase();
		return (['compact', 'wizard'].find(v => v === viewParameter) || null) as TWorkflowViewMode;
	}

	private selectViewMode(forcedViewMode: TWorkflowViewMode): TWorkflowViewMode {
		if (this.workflowItem?.config?.disableWizard)
			return 'compact';

		return forcedViewMode || this.workflowItem?.recommendedViewMode || 'wizard';
	}

	private buildNavTree(workflowItem: IQuestionnaireWorkflowItem): void {
		this.navTree = this.navTreeBuilder.buildNavTree(workflowItem);
	}

	private navigateToParticipantsDashboard(): void {
		this.router.navigate([participantPages.dashboard.base()]).catch(console.error);
	}
}
